import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Link } from 'react-router-dom';

import api from '@/services/api';
import { useAlignmentRoadmapScope } from '@/components/alignment/AlignmentRoadmapScopeContext';
import { useLanguage } from '@/state/language/LanguageProvider';

interface AlignmentMetrics {
  total_visions: number;
  total_strategies: number;
  total_objectives: number;
  poorly_defined_objectives: number;
  total_kpis: number;
  total_projects: number;
  vision_strategy_links: number;
  project_strategy_links: number;
  unaligned_strategies: number;
  unaligned_projects: number;
  vision_objective_alignment_percentage: number;
  objective_strategy_alignment_percentage: number;
  strategy_project_alignment_percentage: number;
  project_strategy_alignment_percentage: number;
}

interface AlignmentWidgetProps {
  tenantId: string;
}

export const AlignmentWidget: React.FC<AlignmentWidgetProps> = ({ tenantId }) => {
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState<AlignmentMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { listParams, scopeKey } = useAlignmentRoadmapScope();


  useEffect(() => {
    const fetchAlignmentMetrics = async () => {
      if (!tenantId) {
        return;
      }
      
      if (tenantId === '') {
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/alignment/alignment-score/${tenantId}`, listParams);

        if (response.data.success) {
          setMetrics(response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to fetch alignment metrics');
        }
      } catch (err) {
        console.error('Error fetching alignment metrics:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        // Set default metrics for display
        setMetrics({
          total_visions: 0,
          total_strategies: 0,
          total_objectives: 0,
          poorly_defined_objectives: 0,
          total_kpis: 0,
          total_projects: 0,
          vision_strategy_links: 0,
          project_strategy_links: 0,
          unaligned_strategies: 0,
          unaligned_projects: 0,
          vision_objective_alignment_percentage: 0,
          objective_strategy_alignment_percentage: 0,
          strategy_project_alignment_percentage: 0,
          project_strategy_alignment_percentage: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAlignmentMetrics();
  }, [tenantId, scopeKey]);

  if (loading) {
    return (
      <Card variant="outline">
        <CardContent className="pt-4">
          <div className="animate-pulse space-y-4">
            {/* Main metrics grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-8 bg-[var(--color-border)] bg-opacity-20 rounded mb-2"></div>
                  <div className="h-4 bg-[var(--color-border)] bg-opacity-20 rounded"></div>
                </div>
              ))}
            </div>
            {/* Alignment KPIs section */}
            <div className="space-y-3">
              <div className="h-4 bg-[var(--color-border)] bg-opacity-20 rounded w-32"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="p-3 border border-[var(--color-border)] rounded-lg">
                    <div className="h-4 bg-[var(--color-border)] bg-opacity-20 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--color-border)] bg-opacity-20 rounded mb-2"></div>
                    <div className="h-6 bg-[var(--color-border)] bg-opacity-20 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outline">
        <CardContent className="pt-4">
          <div className="text-center text-[var(--color-error)]">
            <p>Failed to load alignment data</p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              size="sm" 
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const metricsData = metrics || {
    total_visions: 0,
    total_strategies: 0,
    total_objectives: 0,
    poorly_defined_objectives: 0,
    total_kpis: 0,
    total_projects: 0,
    vision_strategy_links: 0,
    project_strategy_links: 0,
    unaligned_strategies: 0,
    unaligned_projects: 0,
    vision_objective_alignment_percentage: 0,
    objective_strategy_alignment_percentage: 0,
    strategy_project_alignment_percentage: 0,
    project_strategy_alignment_percentage: 0
  };

  return (
    <Card variant="outline">
      <CardContent className="pt-4">
        {/* Main Metrics Grid - Visions, Objectives, Strategies, Projects/Transformation */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Visions */}
          <Link to="/alignment#visions" className="text-center p-3 rounded-lg border border-[var(--color-border)] hover:shadow-md transition-shadow cursor-pointer">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {metricsData.total_visions || 0}
            </div>
            <div className="text-sm text-[var(--color-surface-text)] opacity-75">Visions</div>
          </Link>
          
          {/* Objectives */}
          <Link to="/alignment#objectives" className="text-center p-3 rounded-lg border border-[var(--color-border)] hover:shadow-md transition-shadow cursor-pointer">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {metricsData.total_objectives || 0}
            </div>
            <div className="text-sm text-[var(--color-surface-text)] opacity-75">Objectives</div>
          </Link>
          
          {/* Strategies */}
          <Link to="/alignment#strategies" className="text-center p-3 rounded-lg border border-[var(--color-border)] hover:shadow-md transition-shadow cursor-pointer">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {metricsData.total_strategies || 0}
            </div>
            <div className="text-sm text-[var(--color-surface-text)] opacity-75">Strategies</div>
          </Link>
          
          {/* Projects/Transformation */}
          <Link to="/transformations" className="text-center p-3 rounded-lg border border-[var(--color-border)] hover:shadow-md transition-shadow cursor-pointer">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {metricsData.total_projects || 0}
            </div>
            <div className="text-sm text-[var(--color-surface-text)] opacity-75">Projects/Transformation</div>
          </Link>
        </div>

        {/* Alignment KPIs Section */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-[var(--color-surface-text)] mb-3">Alignment KPIs</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Vision-Objectives Alignment */}
            <div className="text-center p-3 border border-[var(--color-border)] rounded-lg">
              <div className="text-lg font-semibold text-[var(--color-surface-text)] mb-1">
                Vision-Objectives
              </div>
              <div className="text-xs text-[var(--color-surface-text)] opacity-75 mb-2">
                Alignment
              </div>
              <span className={`text-sm font-medium px-3 py-1 rounded border-2 ${
                (metricsData.vision_objective_alignment_percentage || 0) >= 90 ? 'border-green-500 text-green-600 bg-green-50' :
                (metricsData.vision_objective_alignment_percentage || 0) >= 75 ? 'border-yellow-500 text-yellow-600 bg-yellow-50' :
                (metricsData.vision_objective_alignment_percentage || 0) >= 60 ? 'border-orange-500 text-orange-600 bg-orange-50' : 
                'border-red-500 text-red-600 bg-red-50'
              }`}>
                {metricsData.vision_objective_alignment_percentage || 0}%
              </span>
            </div>

            {/* Objectives-Strategy Alignment */}
            <div className="text-center p-3 border border-[var(--color-border)] rounded-lg">
              <div className="text-lg font-semibold text-[var(--color-surface-text)] mb-1">
                Objectives-Strategy
              </div>
              <div className="text-xs text-[var(--color-surface-text)] opacity-75 mb-2">
                Alignment
              </div>
              <span className={`text-sm font-medium px-3 py-1 rounded border-2 ${
                (metricsData.objective_strategy_alignment_percentage || 0) >= 90 ? 'border-green-500 text-green-600 bg-green-50' :
                (metricsData.objective_strategy_alignment_percentage || 0) >= 75 ? 'border-yellow-500 text-yellow-600 bg-yellow-50' :
                (metricsData.objective_strategy_alignment_percentage || 0) >= 60 ? 'border-orange-500 text-orange-600 bg-orange-50' : 
                'border-red-500 text-red-600 bg-red-50'
              }`}>
                {metricsData.objective_strategy_alignment_percentage || 0}%
              </span>
            </div>

            {/* Strategy-Project Alignment */}
            <div className="text-center p-3 border border-[var(--color-border)] rounded-lg">
              <div className="text-lg font-semibold text-[var(--color-surface-text)] mb-1">
                Strategy-Project
              </div>
              <div className="text-xs text-[var(--color-surface-text)] opacity-75 mb-2">
                Alignment
              </div>
              <span className={`text-sm font-medium px-3 py-1 rounded border-2 ${
                (metricsData.strategy_project_alignment_percentage || 0) >= 90 ? 'border-green-500 text-green-600 bg-green-50' :
                (metricsData.strategy_project_alignment_percentage || 0) >= 75 ? 'border-yellow-500 text-yellow-600 bg-yellow-50' :
                (metricsData.strategy_project_alignment_percentage || 0) >= 60 ? 'border-orange-500 text-orange-600 bg-orange-50' : 
                'border-red-500 text-red-600 bg-red-50'
              }`}>
                {metricsData.strategy_project_alignment_percentage || 0}%
              </span>
            </div>

            {/* Project-Strategy Alignment */}
            <div className="text-center p-3 border border-[var(--color-border)] rounded-lg">
              <div className="text-lg font-semibold text-[var(--color-surface-text)] mb-1">
                Project-Strategy
              </div>
              <div className="text-xs text-[var(--color-surface-text)] opacity-75 mb-2">
                Alignment
              </div>
              <span className={`text-sm font-medium px-3 py-1 rounded border-2 ${
                (metricsData.project_strategy_alignment_percentage || 0) >= 90 ? 'border-green-500 text-green-600 bg-green-50' :
                (metricsData.project_strategy_alignment_percentage || 0) >= 75 ? 'border-yellow-500 text-yellow-600 bg-yellow-50' :
                (metricsData.project_strategy_alignment_percentage || 0) >= 60 ? 'border-orange-500 text-orange-600 bg-orange-50' : 
                'border-red-500 text-red-600 bg-red-50'
              }`}>
                {metricsData.project_strategy_alignment_percentage || 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Unaligned Items Alert */}
        {((metricsData.unaligned_strategies || 0) > 0 || (metricsData.unaligned_projects || 0) > 0 || (metricsData.poorly_defined_objectives || 0) > 0) && (
          <div className="mb-3 p-3 rounded-lg">
            <div className="flex items-center gap-2 text-[var(--color-error)] mb-2">
              <span className="text-sm">⚠️</span>
              <span className="text-sm font-medium">Unaligned Items Alert</span>
            </div>
            <div className="text-sm text-[var(--color-error)] opacity-80 space-y-1">
              {(metricsData.poorly_defined_objectives || 0) > 0 && (
                <div>• {t('alignment.overview.poorlyDefined', { count: metricsData.poorly_defined_objectives })}</div>
              )}
              {(metricsData.unaligned_strategies || 0) > 0 && (
                <div>• {metricsData.unaligned_strategies || 0} strategies not linked to visions</div>
              )}
              {(metricsData.unaligned_projects || 0) > 0 && (
                <div>• {metricsData.unaligned_projects || 0} projects not linked to strategies</div>
              )}
            </div>
          </div>
        )}


        {/* No Resources Alert */}
        {(metricsData.total_visions || 0) === 0 && (metricsData.total_strategies || 0) === 0 && (metricsData.total_projects || 0) === 0 && (
          <div className="mt-3 p-3 border border-gray-300 rounded-lg bg-white">
            <div className="flex items-center gap-2 text-gray-900 mb-2">
              <span className="text-sm">🚀</span>
              <span className="text-sm font-medium">Get Started with Strategic Alignment</span>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Create your first vision, strategy, or project to begin building strategic alignment.
            </p>
            <div className="flex gap-2">
              <Link to="/alignment/visions">
                <Button size="sm" className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                  Add Vision
                </Button>
              </Link>
              <Link to="/alignment/strategies">
                <Button size="sm" className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                  Add Strategy
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* No Alignment Links Alert */}
        {(metricsData.total_visions || 0) > 0 && (metricsData.total_strategies || 0) > 0 && (metricsData.vision_strategy_links || 0) === 0 && (metricsData.project_strategy_links || 0) === 0 && (
          <div className="mt-3 p-3 border border-[var(--color-warning)] border-opacity-30 rounded-lg">
            <div className="flex items-center gap-2 text-[var(--color-warning)]">
              <span className="text-sm">⚠️</span>
              <span className="text-sm font-medium">No strategic alignment detected</span>
            </div>
            <p className="text-sm text-[var(--color-warning)] opacity-80 mt-1">
              Link your visions to strategies and projects to strategic objectives to improve alignment.
            </p>
            <Link to="/alignment">
              <Button variant="outline" size="sm" className="mt-2 w-full">
                View Alignment Page
              </Button>
            </Link>
          </div>
        )}


      </CardContent>
    </Card>
  );
};
